import type { Brand, Manifest } from '@clawharbor/manifest';
import { contrastSafeOverrides, googleFontHref, tokensToCss } from '@clawharbor/render-core';
import { html, SP } from '../../html.js';
import type { VNode } from '../../html.js';

/**
 * The tenant's own palette, emitted by the production emitter.
 *
 * `tokensToCss` is the same function that paints the hosted WebMCP page and the
 * MCP Apps widget, so this header is not a preview of the brand — it is the
 * brand, rendered by the code that ships it. Reimplementing a swatch strip here
 * would let the dashboard drift from what the surfaces actually serve, and then
 * the header would be decoration.
 *
 * Scoped to `.brandhead`, never `:root`. A harvested palette applied to the whole
 * document would repaint the tool doing the inspecting, and a #051851 canvas
 * behind an editable tool description is how an operator ends up saving text they
 * could not read. `contrastSafeOverrides` is appended for the same reason both
 * surfaces apply it: a palette lifted off someone else's CSS pairs text and
 * ground at 2:1 often enough that a header nobody can read is a real outcome.
 *
 * Only a declared SUBSTITUTE font is ever fetched — `googleFontHref` returns null
 * unless a family sets `substitute.source === 'google'` — so a tenant's
 * proprietary self-hosted face is never requested and never rehosted.
 */

const TOKEN_STYLE_ID = 'ch-dash-tenant-tokens';
const FONT_LINK_ID = 'ch-dash-tenant-font';
const FONT_PRECONNECT_ID = 'ch-dash-tenant-font-pre';

function upsert<T extends HTMLElement>(id: string, tag: string): T {
  const found = document.getElementById(id);
  if (found) return found as T;
  const el = document.createElement(tag);
  el.id = id;
  document.head.appendChild(el);
  return el as T;
}

export function applyBrandTokens(brand: Brand): void {
  const style = upsert<HTMLStyleElement>(TOKEN_STYLE_ID, 'style');
  const scope = '.brandhead';
  const css = `${tokensToCss(brand, scope)}
${contrastSafeOverrides(brand, scope)}`;
  if (style.textContent !== css) style.textContent = css;

  const href = googleFontHref(brand);
  if (!href) return;
  const pre = upsert<HTMLLinkElement>(FONT_PRECONNECT_ID, 'link');
  pre.rel = 'preconnect';
  pre.href = 'https://fonts.gstatic.com/';
  pre.crossOrigin = 'anonymous';
  const link = upsert<HTMLLinkElement>(FONT_LINK_ID, 'link');
  link.rel = 'stylesheet';
  if (link.href !== href) link.href = href;
}

export function clearBrandTokens(): void {
  for (const id of [TOKEN_STYLE_ID, FONT_LINK_ID, FONT_PRECONNECT_ID]) {
    document.getElementById(id)?.remove();
  }
}

/** Wordmark text, never a logo file — the same rule both surfaces enforce. */
function wordmark(identity: Manifest['identity']): string {
  const wm = identity.wordmark;
  if (!wm) return identity.displayName;
  if (wm.case === 'upper') return wm.text.toUpperCase();
  if (wm.case === 'lower') return wm.text.toLowerCase();
  return wm.text;
}

export function BrandHead(props: {
  identity: Manifest['identity'];
  brand: Brand;
  origin: string;
  tools: number;
}): VNode {
  const roles = Object.entries(props.brand.palette).filter(([, token]) => token != null);
  const sans = props.brand.typography.families.sans;
  const face = sans.substitute?.family ?? sans.observed;

  return html`
    <div class="brandhead">
      <div class="brandhead__in">
        <p class="brandhead__wordmark">${wordmark(props.identity)}</p>
        <p class="brandhead__meta">
          ${props.origin}${SP}·${SP}${props.tools} ${props.tools === 1 ? 'tool' : 'tools'}${SP}·${SP}
          ${face}${sans.substitute ? ' (substitute)' : ''}
        </p>
      </div>
      <ul class="brandhead__swatches" aria-label="Palette as served">
        ${roles.slice(0, 12).map(
          ([role, token]) => html`<li class="brandhead__swatch">
            <span
              class="brandhead__chip"
              style=${`background:${token?.value ?? 'transparent'}`}
              aria-hidden="true"
            ></span>
            <span class="brandhead__role">${role}</span>
            <span class="brandhead__hex">${token?.value}</span>
          </li>`,
        )}
      </ul>
    </div>
  `;
}
